import {
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from "react";

import type { Crumb } from "src/components/layout/types";
import {
  MINI_NAV_PEEK_DELAY_MS,
  MINI_NAV_PEEK_GRACE_MS,
} from "src/components/layout/nav/nav-motion";

/** Collapsed, the sidebar is off the window until it peeks under the pointer, or opens as a sheet on a phone. */
export type MiniNavOverlay = "closed" | "peek" | "sheet";

export type MiniNavEvent =
  /** The pointer rested on the collapsed sidebar's toggle, or in the gutter left of the page card. */
  | "peek"
  /** The grace period after the pointer left those and the peeked sidebar. */
  | "leave"
  /** The toggle, where the sidebar can't dock. */
  | "sheet"
  /** Escape, a navigation, or docking the sidebar. */
  | "close";

export const miniNavReducer = (
  state: MiniNavOverlay,
  event: MiniNavEvent,
): MiniNavOverlay => {
  switch (event) {
    case "peek":
      return state === "closed" ? "peek" : state;
    case "leave":
      return state === "peek" ? "closed" : state;
    case "sheet":
      return "sheet";
    case "close":
      return "closed";
  }
};

/** Where a page sits in the product nav: its section, linked to the section's first page, then the row it lights there. */
export type NavPlace = {
  section?: Crumb;
  item?: Crumb;
  /** Whether the item's row is this page, rather than one of its ancestors. */
  isItemCurrent?: boolean;
  /** The section only groups rows in the nav and has no page of its own, so the trail starts at its row. */
  isSectionGroup?: boolean;
};

const isSameLabel = (a: Crumb["label"], b: Crumb["label"]) =>
  typeof a === "string" &&
  typeof b === "string" &&
  a.toLowerCase() === b.toLowerCase();

const linkPath = (link: string | undefined) => link?.split(/[?#]/)[0];

const isLinkUnder = (link: string | undefined, parent: string | undefined) => {
  const path = linkPath(link);
  const parentPath = linkPath(parent)?.replace(/\/+$/, "");
  return Boolean(
    path &&
      parentPath &&
      (path === parentPath || path.startsWith(`${parentPath}/`)),
  );
};

/**
 * A page's crumbs under its nav place, in the nav's own words: "Projects › Tasks › …".
 * A page with no crumbs gets the place itself. A section that only groups rows is left out.
 */
export const placeTrail = (
  crumbs: Crumb[],
  { section, item, isItemCurrent, isSectionGroup }: NavPlace,
): Crumb[] => {
  const lead = section && !isSectionGroup ? [section] : [];
  if (crumbs.length === 0) {
    return item ? [...lead, item] : lead;
  }
  if (!section) return crumbs;
  const ownStart = crumbs.findIndex(
    (crumb) => !isSameLabel(crumb.label, section.label),
  );
  let own = ownStart === -1 ? [] : crumbs.slice(ownStart);
  if (item) {
    const itemIndex = own.findIndex(
      (crumb) => crumb.link && linkPath(crumb.link) === linkPath(item.link),
    );
    if (itemIndex !== -1) {
      own = own.map((crumb, index) =>
        index === itemIndex ? { ...crumb, label: item.label } : crumb,
      );
    } else if (
      !own.some(
        (crumb) =>
          isSameLabel(crumb.label, item.label) ||
          // A crumb under the row's page already sits inside the row, as a tab of a row's page does.
          isLinkUnder(crumb.link, item.link),
      )
    ) {
      // On the item's own page, its row names the page; anywhere under it, the row leads the page's crumbs.
      own = isItemCurrent ? (own.length > 1 ? own : [item]) : [item, ...own];
    }
  }
  return [...lead, ...own];
};

type Point = [number, number];

const inRect = ([x, y]: Point, rect: DOMRect | undefined) =>
  Boolean(
    rect &&
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom,
  );

/** Which side of the line from `a` to `b` a point is on. */
const side = ([x, y]: Point, [ax, ay]: Point, [bx, by]: Point) =>
  (x - bx) * (ay - by) - (ax - bx) * (y - by);

const inTriangle = (point: Point, [a, b, c]: [Point, Point, Point]) => {
  const ab = side(point, a, b);
  const bc = side(point, b, c);
  const ca = side(point, c, a);
  return !((ab < 0 || bc < 0 || ca < 0) && (ab > 0 || bc > 0 || ca > 0));
};

/** Where an element rests, leaving out a running slide, so a pointer the sliding sidebar hasn't reached yet counts as on it. */
const restingRect = (element: HTMLElement | null) =>
  element
    ? new DOMRect(
        element.offsetLeft,
        element.offsetTop,
        element.offsetWidth,
        element.offsetHeight,
      )
    : undefined;

type PeekIntentArgs = {
  state: MiniNavOverlay;
  dispatch: (event: MiniNavEvent) => void;
  /** The gutter left of the page card, full height, which peeks the sidebar on hover and sits right beside it. */
  gutterRef: RefObject<HTMLElement>;
  /** The collapsed sidebar's toggle, wherever the page put it, which peeks the sidebar on hover too. */
  getToggle: () => Element | null;
  panelRef: RefObject<HTMLElement>;
  /** A list the peeked sidebar opened, like the workspace switcher's, has focus, so leaving for it keeps the sidebar. */
  isHeld: MutableRefObject<boolean>;
};

/**
 * Hover intent for the peek, mouse only: touch and keyboard use the toggle.
 * Moving onto the toggle or the gutter and resting there peeks after a delay.
 * Leaving them and the sidebar closes it after a grace period, so a short slip off it never does.
 */
export const usePeekIntent = ({
  state,
  dispatch,
  gutterRef,
  getToggle,
  panelRef,
  isHeld,
}: PeekIntentArgs) => {
  const armTimer = useRef(0);
  // After a deliberate close, a pointer still on a trigger must leave it before a hover can peek again.
  const isSuppressed = useRef(false);
  // Where the pointer last moved while the sidebar peeked, since a trigger under a moving pointer may not report hover.
  const lastPoint = useRef<Point | null>(null);
  // Where the pointer last was on a trigger: the apex of the triangle it can cross to the panel without closing it.
  const apex = useRef<Point | null>(null);
  const triggerRects = useCallback(
    () => [
      gutterRef.current?.getBoundingClientRect(),
      getToggle()?.getBoundingClientRect(),
    ],
    [gutterRef, getToggle],
  );
  const suppress = useCallback(() => {
    const point = lastPoint.current;
    isSuppressed.current = Boolean(
      gutterRef.current?.matches(":hover") ||
        getToggle()?.matches(":hover") ||
        (point && triggerRects().some((rect) => inRect(point, rect))),
    );
  }, [gutterRef, getToggle, triggerRects]);

  const disarm = () => {
    window.clearTimeout(armTimer.current);
    armTimer.current = 0;
  };

  const arm = (event: ReactPointerEvent) => {
    if (event.pointerType !== "mouse") return;
    // A pointer resting where a trigger appears, as at load, gets events with no movement; only a real move peeks.
    if (event.movementX === 0 && event.movementY === 0) return;
    apex.current = [event.clientX, event.clientY];
    if (state !== "closed" || isSuppressed.current || armTimer.current) return;
    armTimer.current = window.setTimeout(() => {
      armTimer.current = 0;
      dispatch("peek");
    }, MINI_NAV_PEEK_DELAY_MS);
  };

  useEffect(() => {
    if (state !== "peek") return;
    let graceTimer = 0;
    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      const point: Point = [event.clientX, event.clientY];
      const previous = lastPoint.current;
      lastPoint.current = point;
      const isOnTrigger = triggerRects().some((rect) => inRect(point, rect));
      if (isOnTrigger) apex.current = point;
      const panel = restingRect(panelRef.current);
      // From the toggle above the panel, a pointer still moving down into the panel crosses the bar between them.
      const isHeadingToPanel = Boolean(
        apex.current &&
          panel &&
          previous &&
          point[1] > previous[1] &&
          inTriangle(point, [
            apex.current,
            [panel.left, panel.top],
            [panel.right, panel.top],
          ]),
      );
      const startGrace = () => {
        graceTimer = window.setTimeout(() => {
          graceTimer = 0;
          if (!isHeld.current) dispatch("leave");
        }, MINI_NAV_PEEK_GRACE_MS);
      };
      if (isOnTrigger || inRect(point, panel)) {
        window.clearTimeout(graceTimer);
        graceTimer = 0;
      } else if (isHeadingToPanel) {
        // The grace starts over with each step toward the panel, so a pointer that stops short still closes it.
        window.clearTimeout(graceTimer);
        startGrace();
      } else if (!graceTimer) {
        startGrace();
      }
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      document.removeEventListener("pointermove", onMove);
      window.clearTimeout(graceTimer);
      lastPoint.current = null;
      apex.current = null;
    };
  }, [state, dispatch, triggerRects, panelRef, isHeld]);

  useEffect(() => () => window.clearTimeout(armTimer.current), []);

  return {
    onPointerEnter: arm,
    onPointerMove: arm,
    onPointerLeave: (event: ReactPointerEvent) => {
      // A pointer that overshoots the gutter off the window's left edge still peeks, under the browser's own side panel.
      if (event.clientX > 0) disarm();
      isSuppressed.current = false;
    },
    suppress,
    // A press means a click, so no peek opens under it until the pointer leaves and comes back.
    onPointerDown: () => {
      disarm();
      isSuppressed.current = true;
    },
  };
};
